import { QueryString } from '@helpers/QueryString';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

class SurfaceSelector {
  private element: HTMLElement;
  private queryString: QueryString = new QueryString();
  private startStep: HTMLElement | null = null;
  private leftPanel: HTMLElement | null = null;
  private rightPanel: HTMLElement | null = null;
  private spinner: HTMLElement | null = null;

  constructor(element: HTMLElement) {
    this.element = element;
  }

  init() {
    this.bindEvents();
    this.handleStart();
    this.cacheDomElements();
    this.showDefaultStateView();
    this.addTooltipContent();
    this.checkAvailableSpaceForTooltip();
  }

  private bindEvents() {
    this.listenBackButtonEvent();
    this.listenStartOverButtonEvent();
    this.listenStepChangeEvent();
    this.listenStepOptionEvent();
    this.listenWindowResize();
  }

  listenWindowResize() {
    const resizeObserver = new ResizeObserver(() => {
      this.checkAvailableSpaceForTooltip();
    });

    resizeObserver.observe(this.element);
  }

  checkAvailableSpaceForTooltip() {
    const contents = this.element.querySelectorAll<HTMLElement>('.arjo-o-surface-selector-left-panel__tooltip');

    contents.forEach((content) => {
      const hadBottom = content.classList.contains('bottom');

      if (hadBottom) {
        content.classList.remove('bottom');
      }

      const rect = content.getBoundingClientRect();

      const isOffscreenLeft = rect.left < 20;

      content.classList.toggle('bottom', isOffscreenLeft);
    });
  }

  addTooltipContent() {
    const tooltipTriggers = this.element.querySelectorAll<HTMLElement>('.arjo-o-surface-selector-left-panel__tooltip-trigger[data-title]');

    tooltipTriggers.forEach((trigger) => {
      const title = trigger.dataset.title;
      if (!title) return;

      trigger.insertAdjacentHTML('beforeend', `<span class="arjo-o-surface-selector-left-panel__tooltip">${title}</span>`);
      trigger.removeAttribute('data-title');
    });
  }

  async handlePdfVersion() {
    const clonedElement = this.element.cloneNode(true) as HTMLElement;

    await this.inlineSvgUses(clonedElement);

    clonedElement.querySelector('.arjo-o-surface-selector-spinner')?.classList?.remove('show');

    clonedElement.classList.add('pdf-renderer');

    const heroBannerImg = clonedElement.querySelector<HTMLImageElement>('.arjo-m-hero-banner__background img');

    if (heroBannerImg) {
      const box = document.createElement('div');
      box.style.width = '100%';
      box.style.height = '100%';
      box.style.background = `
    url("${heroBannerImg.src}") center / cover no-repeat
  `;
      box.style.borderRadius = getComputedStyle(heroBannerImg).borderRadius;

      heroBannerImg.replaceWith(box);
    }

    const PDF_WIDTH = 1920;
    document.body.appendChild(clonedElement);
    const canvas = await html2canvas(clonedElement, {
      backgroundColor: '#f7f4f3',
      scale: 1,
      width: PDF_WIDTH,
      windowWidth: PDF_WIDTH,
      useCORS: true,
    });

    const imgData = canvas.toDataURL('image/png');

    document.body.removeChild(clonedElement);

    const img = new Image();
    img.src = imgData;

    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
    });

    const imgWidthPx = PDF_WIDTH;
    const imgHeightPx = img.height;
    const pxToMm = (px: number) => px * 0.264583;
    const pdfWidth = pxToMm(imgWidthPx);
    const pdfHeight = pxToMm(imgHeightPx);

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [pdfWidth, pdfHeight],
    });

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    const url = new URL(window.location.href);
    pdf.save(`${url.searchParams.get('result')}-${new Date().toLocaleDateString()}`);
    this.spinner && this.goToView({ from: [this.spinner] });
  }

  private async inlineSvgUses(container: HTMLElement) {
    const useElements = container.querySelectorAll('svg use');

    const spriteCache = new Map<string, Document>();

    for (const use of useElements) {
      const href = use.getAttribute('xlink:href') || use.getAttribute('href');
      if (!href) continue;

      const [spriteUrl, symbolId] = href.split('#');
      if (!symbolId) continue;

      let svgDoc = spriteCache.get(spriteUrl);
      if (!svgDoc) {
        try {
          const response = await fetch(spriteUrl);
          const svgText = await response.text();
          svgDoc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
          spriteCache.set(spriteUrl, svgDoc);
        } catch (err) {
          console.warn(`Failed to fetch SVG: ${spriteUrl}`, err);
          continue;
        }
      }

      const symbol = svgDoc.querySelector(`#${CSS.escape(symbolId)}`);
      if (!symbol) {
        console.warn(`Symbol #${symbolId} not found in ${spriteUrl}`);
        continue;
      }

      const svg = use.closest('svg');
      if (!svg) continue;

      const viewBox = symbol.getAttribute('viewBox');
      if (viewBox) svg.setAttribute('viewBox', viewBox);

      Array.from(symbol.children).forEach((child) => {
        svg.appendChild(child.cloneNode(true));
      });

      use.remove();
    }
  }

  private cacheDomElements() {
    this.startStep = this.element.querySelector<HTMLElement>('.arjo-o-surface-selector-left-panel__start');
    this.leftPanel = this.element.querySelector<HTMLElement>('.arjo-o-surface-selector-left-panel');
    this.rightPanel = this.element.querySelector<HTMLElement>('.arjo-o-surface-selector-right-panel');
    this.spinner = this.element.querySelector<HTMLElement>('.arjo-o-surface-selector-spinner');
  }

  private syncRightPanel(stepId: string) {
    if (!this.rightPanel) return;

    const allGuides = this.rightPanel.querySelectorAll<HTMLElement>('.arjo-o-surface-selector-right-panel__guide');

    allGuides.forEach((guide) => {
      if (guide.dataset.stepId === stepId) {
        guide.classList.add('show');
      } else {
        guide.classList.remove('show');
      }
    });
  }

  private showDefaultStateView() {
    const searchParams = new URL(window.location.href).searchParams;
    const stepParams = searchParams.get('step');
    const resultParams = searchParams.get('result');

    if (!this.startStep) return;

    if (stepParams || resultParams) {
      this.goToView({ from: [this.startStep] });
      this.toggleHeaderButtons('show');
    } else {
      this.spinner && this.goToView({ from: [this.spinner] });
    }

    if (stepParams) {
      this.spinner && this.goToView({ from: [this.spinner] });
      this.goToNextStep([this.startStep], stepParams);
      this.syncRightPanel(stepParams);
      return;
    }

    if (resultParams) {
      const url = new URL(window.location.href);

      if (url.searchParams.get('pdf') !== 'true') {
        this.spinner && this.goToView({ from: [this.spinner] });
        this.goToResultView([this.startStep], resultParams);
        return;
      }

      this.goToResultView([this.startStep], resultParams);
      setTimeout(() => {
        // Do not let an unhandled rejection bubble
        this.handlePdfVersion().catch((err) => {
          console.error('PDF generation failed (caught):', err);
          // restore spinner or make sure UI is consistent
          if (this.spinner) {
            try {
              this.goToView({ from: [this.spinner] });
            } catch (e) {
              console.warn('Failed to restore spinner after PDF error', e);
            }
          }
        });
      }, 0);
    }
  }

  private goToView({
    from = [],
    to = [],
    hide = [],
    show = [],
  }: {
    from?: (HTMLElement | null)[];
    to?: (HTMLElement | null)[];
    hide?: (HTMLElement | null)[];
    show?: (HTMLElement | null)[];
  }) {
    [...hide, ...from].forEach((el) => el && this.toggleStep(el, 'hide'));
    [...show, ...to].forEach((el) => el && this.toggleStep(el, 'show'));
  }

  private changeUrlQuery({ value, type }: { value: string; type: 'step' | 'result' }) {
    this.queryString.deleteQuery(type === 'step' ? 'result' : 'step');

    window.history.pushState(null, '', this.queryString.getUrlQueryString(type, value, true).href);
  }

  private listenStepChangeEvent() {
    this.element.addEventListener('surface-selector-step-change', (e) => {
      this.changeUrlQuery(e.detail);
    });
  }

  private handleStart() {
    const startButton = this.element.querySelector<HTMLButtonElement>(
      '.arjo-o-surface-selector-left-panel__start-button:not([data-event-attached="true"])'
    );

    if (!startButton) return;

    startButton.dataset.eventAttached = 'true';

    startButton.addEventListener('click', () => {
      if (!this.leftPanel || !this.startStep) return;

      const firstStepId = this.leftPanel.dataset.firstStepId;

      if (!firstStepId) return;

      this.hideAllSteps();

      const stepOptionsElement = this.leftPanel.querySelector<HTMLElement>(`.arjo-o-surface-selector-left-panel__step-options-${firstStepId}`);
      const stepContentElement = this.leftPanel.querySelector<HTMLElement>(`.arjo-o-surface-selector-left-panel__step-content-${firstStepId}`);

      if (!stepOptionsElement || !stepContentElement) return;

      this.leftPanel.dataset.currentStep = firstStepId;

      this.dispatchChangeQueryEvent({ value: firstStepId, type: 'step' });

      this.goToView({ from: [this.startStep], to: [stepOptionsElement] });
      this.goToView({ from: [this.startStep], to: [stepContentElement] });

      this.syncRightPanel(firstStepId);

      this.toggleHeaderButtons('show');
    });
  }

  private dispatchChangeQueryEvent({ value, type }: { value: string; type: 'step' | 'result' }) {
    this.element.dispatchEvent(new CustomEvent('surface-selector-step-change', { detail: { value, type } }));
  }

  private hideAllSteps() {
    const stepOptionElements = this.element.querySelectorAll<HTMLElement>('.arjo-o-surface-selector-left-panel__step-options');
    const stepContentElements = this.element.querySelectorAll<HTMLElement>('.arjo-o-surface-selector-left-panel__step-content');

    this.goToView({ hide: Array.from(stepOptionElements).concat(Array.from(stepContentElements)) });
  }

  private toggleStep(step: HTMLElement, state: 'show' | 'hide') {
    if (state === 'hide') {
      step.classList.remove('show');
      return;
    }

    step.classList.add('show');
  }

  private toggleHeaderButtons(state: 'show' | 'hide') {
    const headerButtons = this.element.querySelectorAll<HTMLButtonElement>('.arjo-o-surface-selector-header__buttons .arjo-a-button');

    headerButtons.forEach((button) => {
      if (state === 'show') {
        button.disabled = false;
        return;
      }

      button.disabled = true;
    });
  }

  private listenBackButtonEvent() {
    const backButton = this.element.querySelector<HTMLButtonElement>(
      '.arjo-o-surface-selector-header__back-button:not([data-event-attached="true"])'
    );
    if (!backButton) return;

    backButton.dataset.eventAttached = 'true';

    backButton.addEventListener('click', () => {
      const currentResult = this.element.querySelector<HTMLElement>('.arjo-o-surface-selector-left-panel__results.show');
      this.element.classList.remove('in-result');

      if (!currentResult) {
        this.onStepBack();
      } else {
        this.onResultBack(currentResult);
      }
    });
  }

  private onStepBack() {
    const activeOptionStep = this.element.querySelector<HTMLElement>('.arjo-o-surface-selector-left-panel__step-options.show');
    const activeContentStep = this.element.querySelector<HTMLElement>('.arjo-o-surface-selector-left-panel__step-content.show');

    if (!activeContentStep) return;

    const prevStepId = (activeOptionStep && activeOptionStep.dataset.prevStepId) || activeContentStep.dataset.prevStepId;

    if (!prevStepId) return;

    const prevElements = this.element.querySelectorAll<HTMLElement>(`[data-step-id="${prevStepId}"]`);
    const prevStepOptionElement = this.element.querySelector<HTMLElement>(
      `.arjo-o-surface-selector-left-panel__step-options[data-step-id="${prevStepId}"]`
    );
    const prevStepContentElement = this.element.querySelector<HTMLElement>(
      `.arjo-o-surface-selector-left-panel__step-content[data-step-id="${prevStepId}"]`
    );

    this.hideAllSteps();

    if (prevStepOptionElement && prevStepContentElement) {
      // Normal step
      this.goToView({ from: [activeOptionStep], to: [prevStepOptionElement] });
      this.goToView({ from: [activeContentStep], to: [prevStepContentElement] });
    } else if (!prevStepOptionElement && !prevStepContentElement && prevElements.length > 0) {
      // Start
      this.goToView({ from: [activeOptionStep], to: Array.from(prevElements) });
    }

    this.syncRightPanel(prevStepId);

    if (prevStepId === 'start') {
      this.toggleHeaderButtons('hide');
      this.dispatchChangeQueryEvent({ value: '', type: 'step' });
    } else {
      this.dispatchChangeQueryEvent({ value: prevStepId, type: 'step' });
    }
  }

  private onResultBack(currentResult: HTMLElement) {
    const resultId = currentResult.dataset.resultId;

    if (!resultId) return;

    const prevStepOptions = this.element
      .querySelector<HTMLElement>(`.arjo-o-surface-selector-left-panel__option[data-target-result-id="${resultId}"]`)
      ?.closest<HTMLElement>('.arjo-o-surface-selector-left-panel__step-options');

    const prevStepContent = prevStepOptions
      ?.closest<HTMLElement>('.arjo-o-surface-selector-left-panel__step')
      ?.querySelector<HTMLElement>(`.arjo-o-surface-selector-left-panel__step-content[data-step-id="${prevStepOptions.dataset.stepId}"]`);

    const prevStepId = prevStepOptions?.dataset.stepId;

    if (!prevStepOptions || !prevStepId || !prevStepContent) return;

    this.goToView({ to: [prevStepOptions] });
    this.goToView({ to: [prevStepContent] });

    this.hideResultAndShowGuide();
    this.dispatchChangeQueryEvent({ value: prevStepId, type: 'step' });
  }

  private listenStartOverButtonEvent() {
    const startOverButton = this.element.querySelector<HTMLButtonElement>(
      '.arjo-o-surface-selector-header__start-over-button:not([data-event-attached="true"])'
    );
    if (!startOverButton) return;

    startOverButton.dataset.eventAttached = 'true';

    startOverButton?.addEventListener('click', () => {
      if (!this.leftPanel || !this.startStep) return;

      this.element.classList.remove('in-result');

      this.clearUrlQueryParams();

      this.leftPanel.removeAttribute('data-current-step');

      this.hideAllSteps();
      this.hideResultAndShowGuide();

      this.goToView({ to: [this.startStep] });
      this.syncRightPanel('start');
      this.toggleHeaderButtons('hide');
    });
  }

  private clearUrlQueryParams() {
    this.queryString.deleteQuery('step');

    this.queryString.deleteQuery('result');

    window.history.pushState(null, '', this.queryString.url.href);
  }

  private hideResultAndShowGuide() {
    const recommendEls = this.element.querySelectorAll<HTMLElement>('.arjo-o-surface-selector-right-panel__recommend');
    recommendEls.forEach((el) => {
      const recommendSelectionEl = el?.querySelectorAll<HTMLElement>(`.arjo-o-surface-selector-recommend-section.show`);

      this.goToView({ hide: [el, ...recommendSelectionEl] });
    });

    const guideEl = this.element.querySelector<HTMLElement>('.arjo-o-surface-selector-right-panel__guide-container');
    const resultEl = this.element.querySelector<HTMLElement>('.arjo-o-surface-selector-left-panel__results.show');
    const additionalEl = this.element.querySelector<HTMLElement>('.arjo-o-surface-selector-additional-content');

    if (!guideEl || !resultEl) return;

    this.goToView({ show: [guideEl], hide: [resultEl, additionalEl] });

    this.toggleOffExpander();
  }

  private toggleOffExpander() {
    const expanderInput = this.element.querySelector<HTMLElement>('input.arjo-m-button-expander__input');

    expanderInput?.dispatchEvent(new Event('change'));
  }

  private listenStepOptionEvent() {
    this.element.addEventListener('click', (e) => {
      const activeStepOption = this.element.querySelector<HTMLElement>('.arjo-o-surface-selector-left-panel__step-options.show');

      if (!activeStepOption) return;

      const target = e.target as HTMLElement;
      const option = target.closest<HTMLElement>('.arjo-o-surface-selector-left-panel__option');

      if (
        (option && option.matches('.arjo-o-surface-selector-left-panel__option')) ||
        target.matches('.arjo-o-surface-selector-left-panel__option')
      ) {
        const el = (option ?? target);

        this.handleOptionSelection(el);

        const optionTextContent = el.dataset.trackingText;

        window.GA4.analyticsGa4.CTATracker.pushCTA('Button', 'CTA block', optionTextContent?.trim() || '', el.className);
      }
    });
  }

  private goToNextStep(currentStepEl: HTMLElement[], nextStepId: string) {
    const nextStepEl = this.element.querySelectorAll<HTMLElement>(`[data-step-id="${nextStepId}"]`);

    if (!nextStepEl) return;

    this.goToView({ from: currentStepEl, to: Array.from(nextStepEl) });
    this.syncRightPanel(nextStepId);
  }

  private goToResultView(currentStepEl: HTMLElement[], resultId: string) {
    const resultEl = this.element.querySelector<HTMLElement>(`[data-result-id="${resultId}"]`);
    const guideEl = this.element.querySelector<HTMLElement>('.arjo-o-surface-selector-right-panel__guide-container');
    const recommendEls = this.element.querySelectorAll<HTMLElement>('.arjo-o-surface-selector-right-panel__recommend');
    const recommendSelectionEl = this.element?.querySelectorAll<HTMLElement>(
      `.arjo-o-surface-selector-recommend-section[data-recommend-id="${resultId}"]`
    );
    const additionalEl = this.element.querySelector<HTMLElement>('.arjo-o-surface-selector-additional-content');

    if (!resultEl || !guideEl || !recommendEls || !recommendSelectionEl) return;

    const desktopRecommendSection = this.rightPanel?.querySelector<HTMLAnchorElement>(
      `.arjo-o-surface-selector-right-panel__recommend--desktop .arjo-o-surface-selector-recommend-section[data-recommend-id="${resultId}"]`
    );

    if (desktopRecommendSection) {
      this.handleAssignDesktopResultButtonsToMobile(desktopRecommendSection);
    }

    this.goToView({ hide: [...currentStepEl, guideEl], show: [resultEl, ...recommendEls, ...recommendSelectionEl, additionalEl] });
    this.element.classList.add('in-result');
  }

  private handleAssignDesktopResultButtonsToMobile(desktopRecommendSection: HTMLElement) {
    const contactUsDesktop = desktopRecommendSection.querySelector<HTMLAnchorElement>(`.arjo-o-surface-selector-recommend-section__contact-us`);
    const sendEmailDesktop = desktopRecommendSection.querySelector<HTMLAnchorElement>(`.arjo-o-surface-selector-recommend-section__send-email`);
    const contactUsMobile = this.element.querySelector<HTMLAnchorElement>(
      '.arjo-o-surface-selector-right-panel__recommend--mobile .arjo-o-surface-selector-recommend-section__contact-us'
    );
    const sendEmailMobile = this.element.querySelector<HTMLAnchorElement>(
      '.arjo-o-surface-selector-right-panel__recommend--mobile .arjo-o-surface-selector-recommend-section__send-email'
    );

    if (contactUsDesktop && contactUsMobile) {
      contactUsMobile.href = contactUsDesktop.href;
    }

    if (sendEmailDesktop && sendEmailMobile) {
      sendEmailMobile.href = sendEmailDesktop.href;
    }
  }

  private handleOptionSelection(option: HTMLElement) {
    const nextStepId = option.dataset.nextStepId;
    const resultId = option.dataset.targetResultId;
    const currentStepOptionsEl = option.closest<HTMLElement>('.arjo-o-surface-selector-left-panel__step-options');
    const currentStepContentEl = currentStepOptionsEl
      ?.closest<HTMLElement>('.arjo-o-surface-selector-left-panel__step')
      ?.querySelector?.<HTMLElement>(`.arjo-o-surface-selector-left-panel__step-content[data-step-id="${currentStepOptionsEl.dataset.stepId}"]`);

    if (!currentStepOptionsEl || !currentStepContentEl) return;

    if (nextStepId) {
      this.goToNextStep([currentStepOptionsEl, currentStepContentEl], nextStepId);
      this.dispatchChangeQueryEvent({ value: nextStepId, type: 'step' });
    } else if (resultId) {
      this.goToResultView([currentStepOptionsEl, currentStepContentEl], resultId);
      this.dispatchChangeQueryEvent({ value: resultId, type: 'result' });
    }
  }
}

window.addEventListener('DOMContentLoaded', () => {
  const elements = document.querySelectorAll<HTMLElement>('.arjo-o-surface-selector');

  elements.forEach((element) => new SurfaceSelector(element).init());
});

export {};
